/**
 * Guest diagnostic → account hand-off.
 *
 * The onboarding diagnostic is taken as a guest; both apps stash the result on
 * the device (mobile AsyncStorage, web localStorage) and, after the user signs
 * up OR signs in, decide whether to apply it to the account. This is that
 * decision, shared so web and mobile can't drift (they had: mobile discarded
 * the result for existing accounts, web wrote it into whatever exam the account
 * had selected — corrupting another exam's scores).
 *
 * The result is applied — and the account is switched to the assessed exam —
 * only when ALL hold:
 *   1. a stash exists with an exam key and creation time;
 *   2. it is younger than DIAGNOSTIC_HANDOFF_TTL_MS;
 *   3. the sign-in came through the diagnostic flow (the caller's shared-device
 *      guard: mobile's fromAssessment route param, web's same-tab session
 *      marker) — so someone else's abandoned guest result on a shared device
 *      never lands in this account;
 *   4. the exam belongs to this brand.
 * New and existing accounts follow the same rule. Anything else is discarded
 * and the user lands on the account's own exam.
 */

export const DIAGNOSTIC_HANDOFF_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * @param {Object}   args
 * @param {{ examKey?: string, createdAt?: number } | null} args.stash
 * @param {number}   args.now                     epoch ms
 * @param {boolean}  args.cameFromDiagnosticFlow  the caller's flow signal
 * @param {string[]} args.validExamKeys           this brand's exam keys
 * @returns {{ apply: boolean, examKey: string|null,
 *             reason: 'ok'|'no_stash'|'expired'|'no_flow_signal'|'unknown_exam' }}
 */
export function resolveDiagnosticHandoff({ stash, now, cameFromDiagnosticFlow, validExamKeys }) {
  const examKey = stash?.examKey ?? null;
  const createdAt = stash?.createdAt;
  if (!stash || !examKey || typeof createdAt !== 'number' || !Number.isFinite(createdAt)) {
    return { apply: false, examKey: null, reason: 'no_stash' };
  }
  if (now - createdAt >= DIAGNOSTIC_HANDOFF_TTL_MS) {
    return { apply: false, examKey, reason: 'expired' };
  }
  if (!cameFromDiagnosticFlow) {
    return { apply: false, examKey, reason: 'no_flow_signal' };
  }
  if (!Array.isArray(validExamKeys) || !validExamKeys.includes(examKey)) {
    return { apply: false, examKey, reason: 'unknown_exam' };
  }
  return { apply: true, examKey, reason: 'ok' };
}
